import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import { getMembersOverview } from "./apis/get";

const DEFAULT_LIMIT = "20";

// Roles that own a member list — they default to seeing only their own members.
// Everyone else (ADMIN, SUPER_ADMIN, etc.) defaults to seeing all members.
const CALLER_ROLES = new Set([
  "SUPPORT",
  "CUSTOMER_SUPPORT",
  "CALLING_TEAM_LEAD",
]);

const getLoginData = () => {
  try {
    return JSON.parse(localStorage.getItem("data") || "{}");
  } catch (error) {
    return {};
  }
};

const getCurrentAdminUsername = () => getLoginData()?.username || "";

const getCurrentAdminRole = () =>
  String(getLoginData()?.Role || localStorage.getItem("role") || "").toUpperCase();

const isCallerRole = () => CALLER_ROLES.has(getCurrentAdminRole());

// Callers land on their own members; admins/super-admins see everyone by default.
const getDefaultAssignedTo = () => (isCallerRole() ? getCurrentAdminUsername() : "");

const DEFAULT_FILTERS = {
  search: "",
  premium_filter: "premium",
  assigned_to: getDefaultAssignedTo(),
  gender: "",
  city: "",
  state: "",
  created_from: "",
  created_to: "",
  last_match_published_from: "",
  last_match_published_to: "",
  stale_match_published: "",
  stale_instructions: "",
  liked_not_called_48h: "",
  sort_by: "last_match_published_at",
  sort_order: "desc",
  limit: DEFAULT_LIMIT,
  offset: "0",
};

const PREMIUM_FILTER_OPTIONS = [
  { value: "premium", label: "Premium" },
  { value: "non_premium", label: "Non Premium" },
  { value: "all", label: "All" },
];

const GENDER_OPTIONS = [
  { value: "", label: "All genders" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
];

const STALE_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Only stale" },
  { value: "false", label: "Only fresh" },
];

const TABLE_COLUMNS = [
  { key: "name", label: "Name", sortable: true },
  { key: "gender", label: "Gender", sortable: false },
  // { key: "uid", label: "UID", sortable: true },
  { key: "user_id", label: "User ID", sortable: true },
  // { key: "username", label: "Username", sortable: false },
  { key: "premium", label: "Premium", sortable: true },
  { key: "assigned_to", label: "Assigned To", sortable: true },
  { key: "phone_number", label: "Phone", sortable: false },
  // { key: "matrimonial_id", label: "Matrimonial ID", sortable: false },
  { key: "city", label: "City", sortable: false },
  { key: "state", label: "State", sortable: false },
  { key: "total_matches_published", label: "Published", sortable: true },
  { key: "total_matches_unpublished", label: "Unpublished", sortable: true },
  { key: "published_today", label: "Published Today", sortable: true },
  { key: "published_7d", label: "Published 7d", sortable: true },
  { key: "pending_feedback_count", label: "Feedback Pending", sortable: true },
  { key: "whatsapp_delivery", label: "WhatsApp Reach", sortable: false },
  { key: "total_likes", label: "Likes", sortable: true },
  { key: "total_dislikes", label: "Dislikes", sortable: true },
  { key: "liked_not_called_48h_count", label: "Liked No Call 48h", sortable: true },
  { key: "last_match_published_at", label: "Last Match Published", sortable: true },
  { key: "instructions_updated_at", label: "Instructions Updated", sortable: false },
  { key: "notes", label: "Notes", sortable: false },
  { key: "last_premium_purchase_at", label: "Last Premium Purchase", sortable: false },
  { key: "created_at", label: "Created At", sortable: true },
  { key: "last_active", label: "Last Active", sortable: false },
];

const toDisplay = (value, fallback = "--") => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
};

const toCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatDateTime = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const isOlderThanDays = (value, days = 15) => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const thresholdMs = days * 24 * 60 * 60 * 1000;
  return Date.now() - date.getTime() > thresholdMs;
};

const formatNotes = (value) => {
  if (value === undefined || value === null || value === "") {
    return "--";
  }
  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => {
        if (item === undefined || item === null || item === "") return null;
        if (typeof item === "string") return item.trim();
        try {
          return JSON.stringify(item);
        } catch (error) {
          return String(item);
        }
      })
      .filter(Boolean);

    return normalized.length ? normalized.join("\n") : "--";
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const getWhatsappReach = (delivery) => {
  const d = delivery || {};
  const total = toCount(d.total);
  const delivered = toCount(d.delivered);
  const read = toCount(d.read);
  const failed = toCount(d.failed);

  if (total === 0) {
    return { label: "No match sent", tone: "slate", detail: "" };
  }
  if (read > 0) {
    return { label: `Read (${read})`, tone: "emerald", detail: `${total} sent` };
  }
  if (delivered > 0) {
    return { label: `Delivered (${delivered})`, tone: "emerald", detail: `${total} sent` };
  }
  if (failed > 0 && failed >= total) {
    return { label: `All failed (${failed})`, tone: "rose", detail: `${total} attempted` };
  }
  if (failed > 0) {
    return { label: `${failed} failed`, tone: "amber", detail: `${total} sent` };
  }
  return { label: `Sent (${total})`, tone: "blue", detail: "delivery tracking pending" };
};

const isPremiumMember = (row) =>
  row?.is_premium === true ||
  row?.premium === true ||
  row?.premium === "premium" ||
  row?.premium === "PREMIUM";

const getStaleState = (row) => {
  const tags = Array.isArray(row?.highlight_tags) ? row.highlight_tags : [];
  const staleMatch =
    row?.is_last_match_published_stale === true ||
    tags.includes("stale_match_published") ||
    isOlderThanDays(row?.last_match_published_at, 15);
  const staleInstructions =
    row?.is_instructions_stale === true ||
    tags.includes("stale_instructions") ||
    isOlderThanDays(row?.instructions_updated_at, 15);

  const highlightTags = [];
  if (staleMatch) {
    highlightTags.push({
      key: "stale_match_published",
      label: "Stale Match Publish",
      tone: "rose",
    });
  }
  if (staleInstructions) {
    highlightTags.push({
      key: "stale_instructions",
      label: "Stale Instructions",
      tone: "orange",
    });
  }
  if (row?.liked_not_called_48h === true || tags.includes("liked_not_called_48h")) {
    highlightTags.push({
      key: "liked_not_called_48h",
      label: "Liked, No Call 48h",
      tone: "amber",
    });
  }

  return {
    staleMatch,
    staleInstructions,
    likedNotCalled48h: row?.liked_not_called_48h === true || tags.includes("liked_not_called_48h"),
    highlightTags,
    isAnyStale: staleMatch || staleInstructions || row?.liked_not_called_48h === true || tags.includes("liked_not_called_48h"),
  };
};

const hasContent = (value) => formatNotes(value) !== "--";

const DetailBlock = ({ label, value }) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4">
    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</p>
    {hasContent(value) ? (
      <pre className="mt-3 whitespace-pre-wrap break-words font-sans leading-7 text-slate-200">{formatNotes(value)}</pre>
    ) : (
      <p className="mt-3 leading-7 text-slate-500">No data available.</p>
    )}
  </div>
);

const FilterField = ({ label, children, helper }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</span>
    {children}
    {helper ? <span className="mt-2 block text-xs text-slate-500">{helper}</span> : null}
  </label>
);

const Badge = ({ value, tone = "slate" }) => {
  const toneMap = {
    emerald: "border-emerald-500/20 bg-emerald-500/10 text-emerald-300",
    amber: "border-amber-500/20 bg-amber-500/10 text-amber-300",
    blue: "border-sky-500/20 bg-sky-500/10 text-sky-300",
    orange: "border-orange-500/20 bg-orange-500/10 text-orange-300",
    rose: "border-rose-500/20 bg-rose-500/10 text-rose-300",
    slate: "border-slate-700 bg-slate-800/80 text-slate-200",
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${toneMap[tone] || toneMap.slate}`}>
      {value}
    </span>
  );
};

const EmptyState = ({ title, description, action }) => (
  <div className="flex min-h-[260px] flex-col items-center justify-center gap-4 p-8 text-center">
    <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-950/60 p-4 text-slate-300">
      <AlertCircle size={18} />
    </div>
    <div>
      <p className="text-base font-semibold text-white">{title}</p>
      <p className="mt-2 max-w-xl text-sm leading-6 text-slate-400">{description}</p>
    </div>
    {action}
  </div>
);

const TableSkeleton = () => (
  <div className="space-y-3 p-5">
    {Array.from({ length: 8 }).map((_, index) => (
      <div key={index} className="h-12 animate-pulse rounded-2xl bg-slate-900/90" />
    ))}
  </div>
);

const inputClassName =
  "w-full rounded-2xl border border-slate-700 bg-slate-950/90 px-4 py-3 text-sm text-slate-100 outline-none transition placeholder:text-slate-500 focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20";
const selectClassName =
  "w-full rounded-2xl border border-slate-700 bg-slate-950/90 px-4 py-3 text-sm text-slate-100 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-500/20";
const EXPORT_BATCH_SIZE = 200;

const normalizeResponse = (response) => {
  const root = response || {};
  const data = root?.data || {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const pagination = data?.pagination || {};

  return {
    rows,
    pagination: {
      total: Number(pagination?.total || 0),
      count: Number(pagination?.count || rows.length || 0),
      limit: Number(pagination?.limit || DEFAULT_LIMIT),
      offset: Number(pagination?.offset || 0),
      has_more: Boolean(pagination?.has_more),
    },
  };
};

const formatExportDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const stringifyExportValue = (value) => {
  if (value === undefined || value === null || value === "--") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

const mapMemberRowForExport = (row = {}, index = 0) => {
  return {
    Name: stringifyExportValue(row.name),
    Gender: stringifyExportValue(row.gender),
    Phone: stringifyExportValue(row.phone_number),
    City: stringifyExportValue(row.city),
    State: stringifyExportValue(row.state),
  };
};

export default function MembersOverview() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    count: 0,
    limit: Number(DEFAULT_LIMIT),
    offset: 0,
    has_more: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNotesRow, setSelectedNotesRow] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [filters.search]);

  const requestFilters = useMemo(
    () => ({
      search: debouncedSearch,
      premium_filter: filters.premium_filter,
      assigned_to: filters.assigned_to,
      gender: filters.gender,
      city: filters.city,
      state: filters.state,
      created_from: filters.created_from,
      created_to: filters.created_to,
      last_match_published_from: filters.last_match_published_from,
      last_match_published_to: filters.last_match_published_to,
      stale_match_published: filters.stale_match_published,
      stale_instructions: filters.stale_instructions,
      liked_not_called_48h: filters.liked_not_called_48h,
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      limit: Number(filters.limit || DEFAULT_LIMIT),
      offset: Number(filters.offset || 0),
    }),
    [debouncedSearch, filters],
  );

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    setError(null);

    getMembersOverview(requestFilters)
      .then((response) => {
        if (!isActive) return;
        const normalized = normalizeResponse(response);
        setRows(normalized.rows);
        setPagination(normalized.pagination);
      })
      .catch((fetchError) => {
        if (!isActive) return;
        setRows([]);
        setPagination((previous) => ({
          ...previous,
          offset: Number(filters.offset || 0),
          limit: Number(filters.limit || DEFAULT_LIMIT),
          total: 0,
          count: 0,
          has_more: false,
        }));
        setError({
          status: fetchError?.status || 0,
          message: fetchError?.message || "Unable to load members overview.",
        });
      })
      .finally(() => {
        if (!isActive) return;
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [filters.limit, filters.offset, requestFilters, retryNonce]);

  const updateFilters = useCallback((patch, options = {}) => {
    setFilters((previous) => ({
      ...previous,
      ...patch,
      ...(options.resetOffset === false ? {} : { offset: "0" }),
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const handleSort = useCallback((sortKey) => {
    setFilters((previous) => ({
      ...previous,
      sort_by: sortKey,
      sort_order: previous.sort_by === sortKey && previous.sort_order === "desc" ? "asc" : "desc",
      offset: "0",
    }));
  }, []);

  const refetchCurrent = useCallback(() => {
    setRetryNonce((value) => value + 1);
  }, []);

  const fetchAllExportRows = useCallback(async () => {
    const targetTotal = Number(pagination?.total || 0);
    const collectedRows = [];
    let nextOffset = 0;
    let hasMore = true;

    while (hasMore) {
      const response = await getMembersOverview({
        ...requestFilters,
        offset: nextOffset,
        limit: EXPORT_BATCH_SIZE,
      });
      const normalized = normalizeResponse(response);
      const pageRows = normalized.rows;

      collectedRows.push(...pageRows);
      nextOffset += pageRows.length;
      hasMore =
        pageRows.length > 0 &&
        Boolean(normalized.pagination.has_more) &&
        (targetTotal <= 0 || collectedRows.length < targetTotal);
    }

    return collectedRows;
  }, [pagination?.total, requestFilters]);

  const handleDownloadExcel = useCallback(async () => {
    setExportingExcel(true);
    try {
      const exportRows = await fetchAllExportRows();
      if (exportRows.length === 0) {
        window.alert("No members available to export for the current filters.");
        return;
      }

      const XLSX = await import("xlsx");
      const excelRows = exportRows.map(mapMemberRowForExport);
      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      worksheet["!cols"] = Object.keys(excelRows[0] || {}).map((key) => ({
        wch: Math.min(Math.max(key.length + 2, 14), 38),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Members Overview");
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `members-overview-${dateStamp}.xlsx`);
    } catch (downloadError) {
      console.error("Members overview Excel export failed:", downloadError);
      window.alert(downloadError?.message || "Unable to download Excel for the current filters.");
    } finally {
      setExportingExcel(false);
    }
  }, [fetchAllExportRows]);

  const limit = Number(filters.limit || DEFAULT_LIMIT);
  const offset = Number(filters.offset || 0);
  const showingFrom = pagination.total > 0 ? offset + 1 : 0;
  const showingTo = pagination.total > 0 ? Math.min(offset + rows.length, pagination.total) : rows.length;
  const currentPage = Math.floor(offset / Math.max(limit, 1)) + 1;
  const totalPages = pagination.total > 0 ? Math.max(1, Math.ceil(pagination.total / Math.max(limit, 1))) : currentPage;
  const staleMatchesInView = rows.filter((row) => getStaleState(row).staleMatch).length;
  const staleInstructionsInView = rows.filter((row) => getStaleState(row).staleInstructions).length;
  const likedNotCalledInView = rows.filter((row) => getStaleState(row).likedNotCalled48h).length;
  const publishedTodayInView = rows.reduce((sum, row) => sum + toCount(row?.published_today), 0);
  const pendingFeedbackInView = rows.reduce((sum, row) => sum + toCount(row?.pending_feedback_count), 0);
  const waFailedInView = rows.filter((row) => toCount(row?.whatsapp_delivery?.failed) > 0).length;

  const getSortIndicator = (sortKey) => {
    if (filters.sort_by !== sortKey) return "Sort";
    return filters.sort_order === "asc" ? "Asc" : "Desc";
  };

  return (
    <div className="min-h-screen space-y-6 pb-8 text-slate-100">
      <section className="overflow-hidden rounded-[32px] border border-slate-800 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.18),_transparent_35%),radial-gradient(circle_at_top_right,_rgba(34,197,94,0.12),_transparent_34%),linear-gradient(145deg,_rgba(2,6,23,0.98),_rgba(15,23,42,0.94))] shadow-[0_24px_120px_rgba(15,23,42,0.4)]">
        <div className="grid gap-8 px-6 py-7 xl:grid-cols-[1.15fr_0.95fr] xl:px-8">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-300">
              <Users size={12} />
              Members Overview
            </div>
            <h1 className="mt-4 text-3xl font-black tracking-tight text-white md:text-4xl">
              Premium-first member analytics with server-driven filters
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300 md:text-base">
              Review member-level publishing activity, engagement totals, assignment coverage, and premium lifecycle details with server-side filtering, sorting, and pagination.
            </p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
              <span className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-2">Default premium filter: premium</span>
              <span className="rounded-full border border-slate-700 bg-slate-950/70 px-3 py-2">
                Sort: {filters.sort_by} {filters.sort_order}
              </span>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Members Loaded</p>
              <p className="mt-2 text-sm font-medium text-slate-100">{loading ? "Loading..." : rows.length}</p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Total Members</p>
              <p className="mt-2 text-sm font-medium text-slate-100">{pagination.total}</p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Premium Filter</p>
              <p className="mt-2 text-sm font-medium text-slate-100">{toDisplay(filters.premium_filter)}</p>
            </div>
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Date Window</p>
              <p className="mt-2 text-sm font-medium text-slate-100">
                {filters.last_match_published_from || filters.last_match_published_to
                  ? `${filters.last_match_published_from || "Any"} to ${filters.last_match_published_to || "Any"}`
                  : "Any published date"}
              </p>
            </div>
            <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-300">Stale Match Publish</p>
              <p className="mt-2 text-sm font-medium text-white">{loading ? "Loading..." : staleMatchesInView}</p>
            </div>
            <div className="rounded-2xl border border-orange-500/20 bg-orange-500/10 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-orange-300">Stale Instructions</p>
              <p className="mt-2 text-sm font-medium text-white">{loading ? "Loading..." : staleInstructionsInView}</p>
            </div>
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 sm:col-span-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300">Liked, No Call 48h</p>
              <p className="mt-2 text-sm font-medium text-white">{loading ? "Loading..." : likedNotCalledInView}</p>
            </div>
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300">Published Today (in view)</p>
              <p className="mt-2 text-sm font-medium text-white">{loading ? "Loading..." : publishedTodayInView}</p>
            </div>
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-300">Feedback Pending (in view)</p>
              <p className="mt-2 text-sm font-medium text-white">{loading ? "Loading..." : pendingFeedbackInView}</p>
            </div>
            <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 sm:col-span-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-rose-300">Members With WhatsApp Failures (in view)</p>
              <p className="mt-2 text-sm font-medium text-white">{loading ? "Loading..." : waFailedInView}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-800 bg-slate-950/80 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Filters</h2>
            <p className="mt-1 text-sm text-slate-400">
              Search is debounced and every filter, sort, and page transition is handled on the server.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleDownloadExcel}
              disabled={exportingExcel || loading}
              className="inline-flex items-center gap-2 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-200 transition hover:border-emerald-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download size={15} />
              {exportingExcel ? "Downloading..." : "Download Excel"}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-slate-200 transition hover:border-slate-500 hover:text-white"
            >
              Reset filters
            </button>
            <button
              type="button"
              onClick={refetchCurrent}
              className="inline-flex items-center gap-2 rounded-2xl bg-sky-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-sky-400"
            >
              <RefreshCw size={15} />
              Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <FilterField label="Search" helper="Name, UID, user ID, username, phone, or matrimonial ID.">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <input
                type="text"
                value={filters.search}
                onChange={(event) => updateFilters({ search: event.target.value })}
                placeholder="Search members"
                className={`${inputClassName} pl-11`}
              />
            </div>
          </FilterField>

          <FilterField label="Premium Filter">
            <select
              value={filters.premium_filter}
              onChange={(event) => updateFilters({ premium_filter: event.target.value })}
              className={selectClassName}
            >
              {PREMIUM_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Assigned To" helper="Callers default to their own members; admins see all. Type a name or clear to change.">
            <input type="text" value={filters.assigned_to} onChange={(event) => updateFilters({ assigned_to: event.target.value })} placeholder="Assignee name" className={inputClassName} />
          </FilterField>

          <FilterField label="Gender">
            <select value={filters.gender} onChange={(event) => updateFilters({ gender: event.target.value })} className={selectClassName}>
              {GENDER_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="City">
            <input type="text" value={filters.city} onChange={(event) => updateFilters({ city: event.target.value })} placeholder="City" className={inputClassName} />
          </FilterField>

          <FilterField label="State">
            <input type="text" value={filters.state} onChange={(event) => updateFilters({ state: event.target.value })} placeholder="State" className={inputClassName} />
          </FilterField>

          <FilterField label="Created From">
            <input type="date" value={filters.created_from} onChange={(event) => updateFilters({ created_from: event.target.value })} className={inputClassName} />
          </FilterField>

          <FilterField label="Created To">
            <input type="date" value={filters.created_to} onChange={(event) => updateFilters({ created_to: event.target.value })} className={inputClassName} />
          </FilterField>

          <FilterField label="Last Match Published From">
            <input
              type="date"
              value={filters.last_match_published_from}
              onChange={(event) => updateFilters({ last_match_published_from: event.target.value })}
              className={inputClassName}
            />
          </FilterField>

          <FilterField label="Last Match Published To">
            <input
              type="date"
              value={filters.last_match_published_to}
              onChange={(event) => updateFilters({ last_match_published_to: event.target.value })}
              className={inputClassName}
            />
          </FilterField>

          <FilterField label="Stale Match Published" helper="Server filter from the updated members overview API.">
            <select
              value={filters.stale_match_published}
              onChange={(event) => updateFilters({ stale_match_published: event.target.value })}
              className={selectClassName}
            >
              {STALE_FILTER_OPTIONS.map((option) => (
                <option key={`stale-match-${option.value || "all"}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Stale Instructions" helper="Server filter for members whose latest instructions are older than 15 days.">
            <select
              value={filters.stale_instructions}
              onChange={(event) => updateFilters({ stale_instructions: event.target.value })}
              className={selectClassName}
            >
              {STALE_FILTER_OPTIONS.map((option) => (
                <option key={`stale-instructions-${option.value || "all"}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Liked, No Call 48h" helper="Members with liked matches and no phone-call interaction updated within 48 hours.">
            <select
              value={filters.liked_not_called_48h}
              onChange={(event) => updateFilters({ liked_not_called_48h: event.target.value })}
              className={selectClassName}
            >
              {STALE_FILTER_OPTIONS.map((option) => (
                <option key={`liked-no-call-${option.value || "all"}`} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FilterField>
        </div>
      </section>

      {error?.status === 400 ? (
        <section className="rounded-3xl border border-amber-500/20 bg-amber-500/10 px-6 py-5">
          <div className="flex items-start gap-3">
            <div className="rounded-2xl bg-amber-500/10 p-3 text-amber-300">
              <AlertCircle size={18} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-white">Validation message from backend</h2>
              <p className="mt-1 text-sm text-amber-100/90">{error.message}</p>
            </div>
          </div>
        </section>
      ) : null}

      <section className="rounded-3xl border border-slate-800 bg-slate-950/80 shadow-[0_24px_80px_rgba(15,23,42,0.28)]">
        <div className="flex flex-col gap-4 border-b border-slate-800 px-5 py-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Members Table</h2>
            <p className="mt-1 text-sm text-slate-400">Server-side pagination and sorting across member-level summary fields.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
              {pagination.total > 0 ? `Showing ${showingFrom}-${showingTo} of ${pagination.total}` : `${rows.length} rows`}
            </div>
            <select
              value={String(limit)}
              onChange={(event) => updateFilters({ limit: event.target.value, offset: "0" }, { resetOffset: false })}
              className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 outline-none transition focus:border-sky-400"
            >
              {[10, 20, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize} / page
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <EmptyState
            title="Unable to load members overview"
            description={error.message || "The members overview API returned an error for the current filters."}
            action={
              <button
                type="button"
                onClick={refetchCurrent}
                className="inline-flex items-center gap-2 rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-sky-400"
              >
                <RefreshCw size={14} />
                Try again
              </button>
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No members found"
            description="Try broadening the search, relaxing the premium filter, or widening the created and published date windows."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-[3000px] text-left">
                <thead className="bg-slate-900/80">
                  <tr>
                    {TABLE_COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        className="whitespace-nowrap border-b border-slate-800 px-4 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500"
                      >
                        {column.sortable ? (
                          <button
                            type="button"
                            onClick={() => handleSort(column.key)}
                            className="inline-flex items-center gap-2 text-left transition hover:text-slate-200"
                          >
                            <span>{column.label}</span>
                            <span className="rounded-full border border-slate-700 bg-slate-950 px-2 py-0.5 text-[10px] normal-case tracking-normal text-slate-400">
                              {getSortIndicator(column.key)}
                            </span>
                          </button>
                        ) : (
                          column.label
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => {
                    const profileId = row?.user_id || row?.uid;
                    const notesPreview = formatNotes(row?.notes);
                    const instructionsPreview = formatNotes(row?.instructions);
                    const hasDetails = hasContent(row?.notes) || hasContent(row?.instructions);
                    const staleState = getStaleState(row);
                    const rowClassName = staleState.isAnyStale
                      ? "border-b border-slate-900/80 bg-gradient-to-r from-rose-500/[0.08] via-orange-500/[0.05] to-transparent text-sm text-slate-300 transition hover:bg-rose-500/[0.10]"
                      : "border-b border-slate-900/80 text-sm text-slate-300 transition hover:bg-slate-900/50";

                    return (
                      <tr key={profileId || index} className={rowClassName}>
                        <td className="px-4 py-3 align-top">
                          <div className="min-w-[220px]">
                            <p className="font-medium text-white">{toDisplay(row?.name)}</p>
                            {profileId ? (
                              <div className="mt-2 flex flex-wrap gap-2">
                                <Link
                                  to={`/users/user/${profileId}`}
                                  className="inline-flex items-center rounded-full border border-sky-500/20 bg-sky-500/10 px-2.5 py-1 text-[11px] font-semibold text-sky-300 transition hover:bg-sky-500/20"
                                >
                                  Open Profile
                                </Link>
                                <Link
                                  to={`/matches?user_id=${encodeURIComponent(row?.user_id || row?.uid || "")}`}
                                  className="inline-flex items-center rounded-full border border-slate-700 bg-slate-900 px-2.5 py-1 text-[11px] font-semibold text-slate-200 transition hover:border-sky-500 hover:text-white"
                                >
                                  Matches
                                </Link>
                              </div>
                            ) : null}
                            {staleState.highlightTags.length ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {staleState.highlightTags.map((tag) => (
                                  <Badge key={tag.key} value={tag.label} tone={tag.tone} />
                                ))}
                              </div>
                            ) : null}
                          </div>
                        </td>
                        {/* <td className="px-4 py-3">{toDisplay(row?.uid)}</td> */}
                        <td className="px-4 py-3">{toDisplay(row?.gender)}</td>
                        <td className="px-4 py-3">{toDisplay(row?.user_id)}</td>
                        {/* <td className="px-4 py-3">{toDisplay(row?.username)}</td> */}
                        <td className="px-4 py-3">
                          <Badge value={isPremiumMember(row) ? "Premium" : "Non Premium"} tone={isPremiumMember(row) ? "emerald" : "slate"} />
                        </td>
                        <td className="px-4 py-3">
                          <Badge value={row?.assigned_to ? row.assigned_to : "Unassigned"} tone={row?.assigned_to ? "blue" : "amber"} />
                        </td>
                        <td className="px-4 py-3">{toDisplay(row?.phone_number)}</td>
                        {/* <td className="px-4 py-3">{toDisplay(row?.matrimonial_id)}</td> */}
                        <td className="px-4 py-3">{toDisplay(row?.city)}</td>
                        <td className="px-4 py-3">{toDisplay(row?.state)}</td>
                        <td className="px-4 py-3">{toCount(row?.total_matches_published)}</td>
                        <td className="px-4 py-3">{toCount(row?.total_matches_unpublished)}</td>
                        <td className="px-4 py-3">
                          {toCount(row?.published_today) > 0 ? (
                            <Badge value={toCount(row?.published_today)} tone="emerald" />
                          ) : (
                            <span className="text-slate-500">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3">{toCount(row?.published_7d)}</td>
                        <td className="px-4 py-3">
                          {toCount(row?.pending_feedback_count) > 0 ? (
                            <Badge value={`${toCount(row?.pending_feedback_count)} pending`} tone="amber" />
                          ) : (
                            <Badge value="All done" tone="emerald" />
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const reach = getWhatsappReach(row?.whatsapp_delivery);
                            return (
                              <div className="space-y-1">
                                <Badge value={reach.label} tone={reach.tone} />
                                {reach.detail ? (
                                  <p className="whitespace-nowrap text-[11px] text-slate-500">{reach.detail}</p>
                                ) : null}
                                {row?.whatsapp_delivery?.last_sent_at ? (
                                  <p className="whitespace-nowrap text-[11px] text-slate-500">
                                    {formatDateTime(row.whatsapp_delivery.last_sent_at)}
                                  </p>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">{toCount(row?.total_likes)}</td>
                        <td className="px-4 py-3">{toCount(row?.total_dislikes)}</td>
                        <td className="px-4 py-3">
                          <div className="space-y-2">
                            <p>{toCount(row?.liked_not_called_48h_count)}</p>
                            {staleState.likedNotCalled48h ? (
                              <Badge value="No call in 48h" tone="amber" />
                            ) : null}
                            {row?.last_liked_not_called_at ? (
                              <p className="whitespace-nowrap text-xs text-slate-500">
                                Last like: {formatDateTime(row.last_liked_not_called_at)}
                              </p>
                            ) : null}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="space-y-2">
                            <p>{formatDateTime(row?.last_match_published_at)}</p>
                            {staleState.staleMatch ? <Badge value="Older than 15 days" tone="rose" /> : null}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">
                          <div className="space-y-2">
                            <p>{formatDateTime(row?.instructions_updated_at)}</p>
                            {staleState.staleInstructions ? <Badge value="Older than 15 days" tone="orange" /> : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className={`max-w-[280px] space-y-2 rounded-2xl px-3 py-2 ${staleState.staleInstructions ? "border border-orange-500/20 bg-orange-500/[0.06]" : ""}`}>
                            <p className="line-clamp-3 text-sm leading-6 text-slate-300">{notesPreview}</p>
                            {instructionsPreview !== "--" && notesPreview === "--" ? (
                              <p className="line-clamp-3 text-xs leading-6 text-slate-500">{instructionsPreview}</p>
                            ) : null}
                            {hasDetails ? (
                              <button
                                type="button"
                                onClick={() => setSelectedNotesRow(row)}
                                className="inline-flex items-center rounded-full border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-semibold text-slate-200 transition hover:border-sky-500 hover:text-white"
                              >
                                View Details
                              </button>
                            ) : null}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row?.last_premium_purchase_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row?.created_at)}</td>
                        <td className="whitespace-nowrap px-4 py-3">{formatDateTime(row?.last_active)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col gap-4 border-t border-slate-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-400">
                Page <span className="font-semibold text-white">{currentPage}</span>
                {totalPages ? <> of <span className="font-semibold text-white">{totalPages}</span></> : null}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => updateFilters({ offset: String(Math.max(0, offset - limit)) }, { resetOffset: false })}
                  disabled={offset === 0}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft size={14} />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => updateFilters({ offset: String(offset + limit) }, { resetOffset: false })}
                  disabled={!pagination.has_more}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 transition hover:border-sky-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      {selectedNotesRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-slate-950/80">
          <div className="flex h-full w-full max-w-2xl flex-col border-l border-slate-800 bg-slate-950 shadow-[-24px_0_80px_rgba(15,23,42,0.45)]">
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Member Notes & Instructions</p>
                <h3 className="mt-2 text-lg font-semibold text-white">{toDisplay(selectedNotesRow?.name)}</h3>
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400">
                  <span>{toDisplay(selectedNotesRow?.uid)}</span>
                  <span>{toDisplay(selectedNotesRow?.user_id)}</span>
                  <span>{selectedNotesRow?.assigned_to || "Unassigned"}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedNotesRow(null)}
                className="inline-flex items-center justify-center rounded-full border border-slate-700 bg-slate-900 p-2 text-slate-300 transition hover:border-slate-500 hover:text-white"
                aria-label="Close notes drawer"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
              <DetailBlock label="Notes" value={selectedNotesRow?.notes} />
              <DetailBlock label="Instructions" value={selectedNotesRow?.instructions} />
              <div className="rounded-2xl border border-slate-800 bg-slate-900/60 px-4 py-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Instructions Updated At</p>
                <p className="mt-3 leading-7 text-slate-200">{formatDateTime(selectedNotesRow?.instructions_updated_at)}</p>
              </div>
            </div>

            <div className="flex justify-end border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={() => setSelectedNotesRow(null)}
                className="inline-flex items-center rounded-xl bg-sky-500 px-4 py-2 text-sm font-semibold text-slate-950 transition hover:bg-sky-400"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
